// FTEDN Creator — Vote Card (wired with real vote action)
import { useState } from 'react';
import { useToast } from '../../components/ui/Toast';
import { getVoteXP, getVotePower } from './voting.helpers';
import { useUserStore } from '../../stores/user.store';
import './VoteFlipCard.css';

interface VoteCard {
  id: string;
  title: string;
  desc: string;
  votes: number;
  category: string;
  hot?: boolean;
}

interface VoteFlipCardProps {
  card: VoteCard;
  index?: number;
  onVote?: () => Promise<boolean>;
  alreadyVoted?: boolean;
}

export function VoteFlipCard({ card, index = 0, onVote, alreadyVoted = false }: VoteFlipCardProps) {
  const [voted, setVoted] = useState(alreadyVoted);
  const [voteCount, setVoteCount] = useState(card.votes);
  const [showXP, setShowXP] = useState(false);
  const { addToast } = useToast();
  const { profile } = useUserStore();

  const categoryIcons: Record<string, string> = {
    product: '🧴', scent: '🌸', packaging: '📦', ingredient: '🧬'
  };

  const handleVote = async () => {
    if (voted) return;

    // Optimistic update
    setVoted(true);
    setVoteCount(prev => prev + 1);
    setShowXP(true);
    setTimeout(() => setShowXP(false), 1200);

    const power = getVotePower(profile?.level ?? 1);
    const xp = getVoteXP(false, power);

    if (onVote) {
      const ok = await onVote();
      if (!ok) {
        // Revert
        setVoted(false);
        setVoteCount(prev => prev - 1);
        addToast({ type: 'error', title: 'Failed', message: 'Could not cast vote' });
        return;
      }
    }

    addToast({ type: 'xp', title: `+${xp} XP`, message: `Voted for "${card.title}"` });
  };

  return (
    <div className={`vote-card glass-card ${voted ? 'voted' : ''}`}
         style={{ animationDelay: `${index * 0.08}s` }}>
      {card.hot && <div className="vote-hot-badge">🔥 HOT</div>}

      <div className="vote-card-top">
        <span className="vote-category-tag">
          {categoryIcons[card.category] || '📋'} {card.category}
        </span>
      </div>

      <h3 className="vote-title">{card.title}</h3>
      <p className="vote-desc">{card.desc}</p>

      <div className="vote-action-row">
        <button className={`vote-btn ${voted ? 'vote-btn--voted' : ''}`} onClick={handleVote}>
          <span className="vote-btn-icon">{voted ? '✓' : '▲'}</span>
          <span className="vote-btn-count">{voteCount}</span>
        </button>
        <span className="vote-xp-hint">+15 XP</span>
      </div>

      {showXP && (
        <div className="vote-xp-float">+15 XP ⚡</div>
      )}
    </div>
  );
}
